import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSmartLinkAnalytics, fetchSmartLinkList,
  fetchTopThreeSmartLinks,
  createMusicLinkApi,
  fetchSmartLinkDetailsApi,
  updateSmartLinkApi,
  createPreSaveSmartLinkApi,
  updatePreSaveSmartLinkApi,
  fetchOverallLinksAnalyticsApi,
  fetchOverallAudience,
  downloadOverallAudienceCsvApi,
  getArtistFutureSaveApi,
  updateArtistFutureSaveApi,
  createBioAndTourLinkApi,
  checkBioLinkStatusApi,
  updateBioAndTourLinkApi
} from "../../api/artistManager/smartLink.api";
import { showToast } from "../../helper/ShowToast";
import { useArtistManagerActivityTracker } from "../../hooks/useArtistManagerActivityTracker";

export const useSmartLinkAnalytics = ({
  smartLinkId,
  range,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["smartlink-analytics", smartLinkId, range],
    queryFn: () =>
      fetchSmartLinkAnalytics({
        smartLinkId,
        range,
      }),
    enabled: enabled && !!smartLinkId,
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message ||
        "Failed to load analytics"
      );
    },
  });
};

export const useSmartLinkList = ({ page, limit, enabled = true, type } = {}) =>
  useQuery({
    queryKey: ["smartlink-list", page, limit, type],
    queryFn: () => fetchSmartLinkList({ page, limit, type }),
    enabled,
    keepPreviousData: true, // for smooth pagination
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to load smart links"
      );
    },
  });

export const useTopThreeSmartLinks = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ["top-three-smartlinks"],
    queryFn: fetchTopThreeSmartLinks,
    enabled,
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message ||
        "Failed to load top smart links"
      );
    },
  });

/* ---------------- DETAILS ---------------- */

export const useSmartLinkDetails = (smartLinkId, isEditMode) => {
  return useQuery({
    queryKey: ["smartlink-details", smartLinkId],
    queryFn: () => fetchSmartLinkDetailsApi(smartLinkId),
    enabled: isEditMode && !!smartLinkId,
    retry: false,
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to load smartlink details"
      ),
  });
};

/* ---------------- CREATE smartlink---------------- */

export const useCreateMusicLink = ({ navigate }) => {
  const queryClient = useQueryClient();
  const { trackMusicLinkCreated } = useArtistManagerActivityTracker();

  return useMutation({
    mutationFn: createMusicLinkApi,
    onSuccess: () => {
      showToast("success", "Music link created");
      queryClient.invalidateQueries(["smartlink-list"]);
      queryClient.invalidateQueries(["top-three-smartlinks"]);
      trackMusicLinkCreated();
      setTimeout(() => {
        navigate("/admin/artist-manager/link-and-fan-hub");
      }, 1000);
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to create music link"
      ),
  });
};

/* ---------------- UPDATE smartlink---------------- */

export const useUpdateSmartLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSmartLinkApi,
    onSuccess: (data, variables) => {
      showToast("success", "Smart link updated");

      // refresh list
      queryClient.invalidateQueries(["smartlink-list"]);

      // refresh details cache
      queryClient.invalidateQueries([
        "smartlink-details",
        variables.smartLinkId,
      ]);
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to update smart link"
      ),
  });
};

export const useCreatePreSaveSmartLink = ({ navigate }) => {
  const queryClient = useQueryClient();
  const { trackPreSaveLinkCreated } = useArtistManagerActivityTracker();

  return useMutation({
    mutationFn: createPreSaveSmartLinkApi,
    onSuccess: (data) => {
      showToast("success", data.message);
      // refresh lists
      queryClient.invalidateQueries(["smartlink-list"]);
      queryClient.invalidateQueries(["top-three-smartlinks"]);
      trackPreSaveLinkCreated();

      setTimeout(() => {
        navigate("/admin/artist-manager/link-and-fan-hub");
      }, 1000);
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message ||
        error.message ||
        "Failed to create Pre Save link"
      );
    },
  });
};

export const useUpdatePreSaveSmartLink = ({ setCurrentStep }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePreSaveSmartLinkApi,
    onSuccess: (data) => {
      showToast("success", data.message);

      // refresh affected queries
      queryClient.invalidateQueries(["smartlink-details"]);
      queryClient.invalidateQueries(["smartlink-list"]);

      setCurrentStep(1);
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || error.message
      );
    },
  });
};


export const useOverallSmartLinkAnalytics = (range = "all-time", { enabled = true } = {}) => {
  return useQuery({
    queryKey: ["overall-smartlink-analytics", range],
    queryFn: () => fetchOverallLinksAnalyticsApi(range),
    enabled,
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to load analytics"
      );
    },
  });
};

export const useOverallAudience = ({ page, limit, enabled = true }) => {
  return useQuery({
    queryKey: ["overall-audience", page, limit],
    queryFn: () => fetchOverallAudience({ page, limit }),
    enabled,
    keepPreviousData: true,
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to load analytics"
      );
    },
  });
};

export const useDownloadOverallAudienceCsv = () => {

  return useMutation({

    mutationFn: downloadOverallAudienceCsvApi,

    onSuccess: (blob) => {

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "fanbase.csv";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    }
  });
};

// future save
export const useArtistFutureSave = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ["artist-future-save"],
    queryFn: getArtistFutureSaveApi,
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });
};

export const useUpdateArtistFutureSave = () => {
  const queryClient = useQueryClient();
  const { trackFutureSaveSetup } = useArtistManagerActivityTracker();
  return useMutation({
    mutationFn: updateArtistFutureSaveApi,
    onSuccess: (data, variables) => {
      showToast('success', "Future Save settings updated successfully");
      queryClient.invalidateQueries(["artist-future-save"]);
      if (variables?.enabled && variables?.services?.length > 0) {
        trackFutureSaveSetup();
      }
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || "Failed to update Future Save settings"
      );
    }
  });
};

// bio link / tour link
export const useCreateBioAndTourLink = ({ navigate, onSuccess: customOnSuccess } = {}) => {
  const queryClient = useQueryClient();
  const { trackBioLinkCreated } = useArtistManagerActivityTracker();
  return useMutation({
    mutationFn: createBioAndTourLinkApi,
    onSuccess: (res) => {
      const linkType = res?.data?.linkType;
      showToast(
        "success",
        linkType === "tourlink" ? "Your Tour link created" : "Your Bio link created"
      );
      queryClient.invalidateQueries(["smartlink-list"]);
      queryClient.invalidateQueries(["top-three-smartlinks"]);
      if (linkType === "biolink") {
        queryClient.invalidateQueries(["biolink-status"]);
        trackBioLinkCreated();
      }
      if (customOnSuccess) {
        customOnSuccess(res);
        return;
      }
      if (navigate) {
        setTimeout(() => {
          navigate("/admin/artist-manager/link-and-fan-hub");
        }, 1000);
      }
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message?.message || err?.response?.data?.message || "Failed to create link"
      )
  });
};

export const useBioLinkStatus = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ["biolink-status"],
    queryFn: checkBioLinkStatusApi,
    enabled,
    retry: false,
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to check bio link status"
      ),
  });
};


export const useUpdateBioAndTourLink = ({ onSuccess: customOnSuccess } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBioAndTourLinkApi,
    onSuccess: (data, variables) => {
      const linkType = data?.data?.linkType;
      showToast(
        "success",
        linkType === "tourlink" ? "Tour link updated" : "Bio link updated"
      );
      // refresh list
      queryClient.invalidateQueries(["smartlink-list"]);
      // refresh details cache
      queryClient.invalidateQueries([
        "smartlink-details",
        variables.id || variables.smartLinkId,
      ]);
      if (linkType === "biolink") {
        queryClient.invalidateQueries(["biolink-status"]);
      }
      customOnSuccess?.(data, variables);
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to update smart link"
      ),
  });
};



